import { ThinkingOrb, type OrbState as OrbStateName } from "@/components/thinking-orb"
import { cn } from "@/lib/utils"

// ---------------------------------------------------------------------------
// OrbState — orb-animation loading primitive, built on the vendored
// ThinkingOrb engine (src/components/thinking-orb/). Since 2026-08-23 the orb
// IS the general loading idiom: <PageLoader> and <LoadingState> render it too
// (owner ruling). OrbState is the standalone form for direct placements.
//
// The engine ships exactly two tuned canvas sizes (64 and 20 CSS px — see
// thinking-orb/types.ts). `full` reuses the 64 preset's tuning and stretches
// it to 160 CSS px via style, matching the largest surface this primitive is
// used on (kiosk full-screen loaders) without forking the engine's presets.
//
// Accessibility: the wrapper carries role="status" + a visually-hidden label
// (the single announced string). The inner canvas is aria-hidden so its own
// built-in aria-label doesn't double-announce. Reduced-motion and
// hidden/offscreen pausing are handled inside ThinkingOrb itself — nothing
// extra needed here.
// ---------------------------------------------------------------------------

const DISPLAY_PX = { inline: 20, block: 64, full: 160 } as const
const ENGINE_SIZE = { inline: 20, block: 64, full: 64 } as const

export type OrbStateSize = keyof typeof DISPLAY_PX

export interface OrbStateProps {
  /** Engine animation profile to play. */
  state: OrbStateName
  /** `inline` (~20px) · `block` (~64px, default) · `full` (~160px). */
  size?: OrbStateSize
  /** Visible + announced text. Defaults to a generic "Loading…". */
  label?: string
  className?: string
}

function OrbState({ state, size = "block", label = "Loading…", className }: OrbStateProps) {
  const displayPx = DISPLAY_PX[size]
  const engineSize = ENGINE_SIZE[size]

  return (
    <span
      role="status"
      className={cn("inline-flex items-center justify-center", className)}
    >
      <ThinkingOrb
        state={state}
        size={engineSize}
        aria-hidden="true"
        style={engineSize === displayPx ? undefined : { width: displayPx, height: displayPx }}
      />
      <span className="sr-only">{label}</span>
    </span>
  )
}

export { OrbState }
